import apiV2 from "api-v2";
import type {
	FinalScoringDetail,
	FinalCriterionScore,
	SupplierClarificationItem,
	RevisionFile,
	FinalScoringMemberProgress,
} from "../types/scoring.type";

const unwrap = <T>(responseData: unknown): T => {
	if (responseData && typeof responseData === "object" && "data" in responseData) {
		return (responseData as Record<string, unknown>).data as T;
	}
	return responseData as T;
};

const evalBase = (rfxId: number, evalId: number): string =>
	`/rfx/${rfxId}/evaluation/${evalId}`;

export const getFinalScores = async (
	rfxId: number,
	evalId: number,
	inviteId: number
): Promise<FinalScoringDetail> => {
	const response = await apiV2.get(
		`${evalBase(rfxId, evalId)}/final-scores/${inviteId}`
	);
	const raw = unwrap<Record<string, unknown>>(response.data);
	const rawCriteria = (raw.criteria as Record<string, unknown>[]) ?? [];
	return {
		inviteId: raw.invite_id as number,
		supplierName: raw.supplier_name as string,
		isSubmitted: !!raw.is_submitted,
		submittedAt: (raw.submitted_at as string | null) ?? null,
		criteria: rawCriteria.map((c) => ({
			rfxCriteriaId: c.rfx_criteria_id as number,
			criterionName: c.criterion_name as string,
			criterionWeight: c.criterion_weight as number,
			initialScore: (c.initial_score as number | null) ?? null,
			initialComment: (c.initial_comment as string | null) ?? null,
			finalScore: (c.final_score as number | null) ?? null,
			finalComment: (c.final_comment as string | null) ?? null,
			updatedAt: (c.updated_at as string | null) ?? null,
		})),
	};
};

export interface SaveFinalScorePayload {
	final_comment: string | null;
	final_score: number | null;
	rfx_criteria_id: number;
}

export const saveFinalScore = async (
	rfxId: number,
	evalId: number,
	inviteId: number,
	payload: SaveFinalScorePayload
): Promise<void> => {
	await apiV2.put(
		`${evalBase(rfxId, evalId)}/final-scores/${inviteId}`,
		payload
	);
};

export const submitFinalScores = async (
	rfxId: number,
	evalId: number
): Promise<void> => {
	await apiV2.post(`${evalBase(rfxId, evalId)}/final-scores/submit`);
};

export const getSupplierClarifications = async (
	rfxId: number,
	evalId: number,
	inviteId: number
): Promise<SupplierClarificationItem[]> => {
	const response = await apiV2.get(
		`${evalBase(rfxId, evalId)}/clarifications/${inviteId}`
	);
	const raw = unwrap<Record<string, unknown>>(response.data);
	const items = (raw.clarifications as Record<string, unknown>[]) ?? [];
	return items.map((item) => {
		const rawFiles = (item.files as Record<string, unknown>[]) ?? [];
		return {
			id: item.id as number,
			referenceNumber: item.reference_number as number,
			clarificationText: item.clarification_text as string,
			finalResponse: (item.final_response as string | null) ?? null,
			relatesTo: (item.relates_to as string | null) ?? null,
			dateSent: (item.date_sent as string | null) ?? null,
			isComplete: !!item.is_complete,
			impact: (item.impact as string | null) ?? null,
			files: rawFiles.map((f) => ({
				id: f.id as number,
				fileName: f.file_name as string,
				fileType: (f.file_type as string | null) ?? null,
				downloadUrl: (f.download_url as string | null) ?? null,
			})),
			isSent: !!item.is_sent,
			hasChat: !!item.has_chat,
		};
	});
};

export const getSupplierRevisions = async (
	rfxId: number,
	evalId: number,
	inviteId: number
): Promise<RevisionFile[]> => {
	const response = await apiV2.get(
		`${evalBase(rfxId, evalId)}/revisions/${inviteId}`
	);
	const raw = unwrap<Record<string, unknown>>(response.data);
	const items = (raw.revisions as Record<string, unknown>[]) ?? [];
	return items.map((item) => ({
		id: item.id as number,
		fileName: item.file_name as string,
		fileType: (item.file_type as string | null) ?? null,
		revisionType: item.revision_type as string,
		uploadedAt: item.uploaded_at as string,
		uploadedBy: (item.uploaded_by as string | null) ?? null,
		downloadUrl: (item.download_url as string | null) ?? null,
	}));
};

export const getFinalProgress = async (
	rfxId: number,
	evalId: number
): Promise<FinalScoringMemberProgress[]> => {
	const response = await apiV2.get(
		`${evalBase(rfxId, evalId)}/final-scores/progress`
	);
	const raw = unwrap<Record<string, unknown>[]>(response.data) ?? [];
	return raw.map((item) => ({
		memberId: item.member_id as number,
		memberName: (item.member_name as string | null) ?? null,
		email: item.email as string,
		role: item.role as string,
		suppliersCompleted: item.suppliers_completed as number,
		totalSuppliers: item.total_suppliers as number,
		isSubmitted: !!item.is_submitted,
		submittedAt: (item.submitted_at as string | null) ?? null,
	}));
};
